import React, { useEffect, useMemo, useState } from 'react';
import { Col, Modal, Row } from 'antd';
import {
	RBHButton,
	RBHText,
	RBHTextInput,
} from '../../../../../shared/components';
import { textStyles } from '../../../../../shared/theme/fonts-ppv';
import { Colors } from '../../../../../shared/theme';
import RBHTable2 from '../../../../../shared/components/rbh-table-2';
import { SearchOutlined } from '@ant-design/icons';
import './style.css';
import { useDispatch, useSelector } from 'react-redux';
import {
	GENERAL_STATE_KEY,
	generalStateSelector,
} from '../../../../../redux/general/general-reducer';
import { reportCreators } from '../../../Report/redux/report-action';
import { ModifierGroup } from '../../../ModifierGroup/redux/modifier-group-type';
import { MenuModifierGroup } from '@ppvrbh/merchant-frontend-core';
import { DEBOUNCE_TIMEOUT } from '../../../../../shared/helpers/constant';
import { FormattedMessage } from 'react-intl';

interface MenuGroupOptionsModalProps {
	open: boolean;
	menuGroups: ModifierGroup[];
	activeModifierGroups: MenuModifierGroup[];
	onClose: () => void;
	onConfirm: (selectedIds: ModifierGroup[]) => void;
}
export const MenuGroupOptionsModal = ({
	open,
	menuGroups,
	activeModifierGroups,
	onClose,
	onConfirm,
}: MenuGroupOptionsModalProps) => {
	const [displayedMenuGroups, setDisplayedMenuGroups] =
		useState<ModifierGroup[]>(menuGroups);
	const [search, setSearch] = useState('');
	const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(
		activeModifierGroups.map((item) => item.menuModifierGroupId ?? 0),
	);
	const appConfig = useSelector(
		generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG),
	);
	const maxSelected = useMemo(() => {
		if (Array.isArray(appConfig)) {
			return appConfig.find(
				(item) => item.key === 'modifier.group.maximum.per.menu',
			)?.value;
		}
		return undefined;
	}, [appConfig]);
	const dispatch = useDispatch();
	const disabled = useMemo(() => {
		if (
			typeof maxSelected === 'string' &&
			tempSelectedIds.length > Number(maxSelected)
		) {
			return true;
		}
		if (activeModifierGroups.length !== tempSelectedIds.length) {
			return false;
		}
		const defaultIds = activeModifierGroups
			.map((item) => item.menuModifierGroupId ?? 0)
			.sort();
		const sortedTempIds = [...tempSelectedIds].sort();

		return sortedTempIds.every((item, index) => item === defaultIds[index]);
	}, [tempSelectedIds, activeModifierGroups]);

	const handleConfirmClick = () => {
		const data = menuGroups.filter((item) =>
			tempSelectedIds.includes(item.menuModifierGroupId),
		);

		onConfirm(data);
	};

	useEffect(() => {
		if (!maxSelected) {
			dispatch(
				reportCreators.callConfigAndShopList(
					() => {},
					() => {},
				),
			);
		}
	}, []);

	useEffect(() => {
		setDisplayedMenuGroups(menuGroups);
	}, [menuGroups]);

	useEffect(() => {
		if (!menuGroups || menuGroups.length === 0) return;
		const timeout = setTimeout(() => {
			const items = menuGroups.filter(
				(item) =>
					item.menuModifierGroupName?.includes(search) ||
					item.menuModifierGroupNameEN
						?.toLowerCase()
						?.includes(search?.toLowerCase()),
			);
			setDisplayedMenuGroups(items);
		}, DEBOUNCE_TIMEOUT);

		return () => clearTimeout(timeout);
	}, [search]);
	return (
		<Modal
			visible={open}
			wrapClassName='modifier-group-form-modal'
			style={{
				backgroundColor: Colors.white,
				borderRadius: '10px',
				border: 0,
				padding: '24px',
			}}
			bodyStyle={{ textAlign: 'start', width: '720px' }}
			footer={null}
			closable={false}
		>
			<Row align={'middle'} justify='start' style={{ marginBottom: 24 }}>
				<RBHText
					style={{ ...textStyles.h3, color: Colors.grey800 }}
					text={'main.foodmenu.addmenu.modifiergroups.modal.title'}
				/>
			</Row>
			<Row justify='space-between' align='middle'>
				<Col>
					<RBHText
						style={{...textStyles.subtitle1, color: Colors.grey600}}
						id={'main.foodmenu.addmenu.modifiergroups.modal.select'}
						data-testid={'main.foodmenu.addmenu.modifiergroups.modal.select'}
						text={'main.foodmenu.addmenu.modifiergroups.modal.select'}
						values={{
							from: tempSelectedIds.length,
							to: menuGroups.length,
						}}
					/>
				</Col>
				<Col>
					<RBHTextInput
						value={search}
						onChange={(event: any) => setSearch(event.target.value)}
						id='search-modification-item'
						placeholder='main.foodmenu.addmenu.modifiergroups.modal.search'
						prefix={
							<SearchOutlined
								style={{ color: Colors.grey400 }}
								translate={null}
							/>
						}
						style={{ width: '100%', borderRadius: '8px' }}
					/>
				</Col>
			</Row>
			<Row style={{ marginTop: '12px' }}>
				<RBHTable2
					rowSelection={{
						selectedRowKeys: tempSelectedIds,
						onChange: (keys) => setTempSelectedIds(keys.map(Number)),
						columnTitle: <></>,
					}}
					columns={[
						{
							title: <FormattedMessage id="main.menu.modifiergroup.column.name" />,
							dataIndex: 'menuModifierGroupName',
						},
						{
							title: <FormattedMessage id="main.menu.modifiergroup.column.nameEN" />,
							dataIndex: 'menuModifierGroupNameEN',
						},
						{ title: <FormattedMessage id="main.menu.modifiergroup.column.total" />, dataIndex: 'count' },
					]}
					dataSource={displayedMenuGroups.map((item) => ({
						id: item.menuModifierGroupId,
						menuModifierGroupName: item.menuModifierGroupName,
						menuModifierGroupNameEN: item.menuModifierGroupNameEN,
						count: item.menuModifierItems?.length ?? 0,
					}))}
				/>
			</Row>
			{typeof maxSelected === 'string' && (
				<Row style={{ marginTop: '4px', marginBottom: '4px' }}>
					<RBHText
						style={{...textStyles.subtitle3, }}
						text='main.foodmenu.addmenu.modifiergroups.modal.remark'
						values={{
							max: maxSelected,
						}}
					/>
				</Row>
			)}
			<Row style={{ gap: 8 }} justify='end' align='middle'>
				<RBHButton
					style={{
						padding: '10px 24px',
						border: '1px solid #CDCDDC',
						borderRadius: 48,
						color: Colors.grey800,
						height: '100%',
						...textStyles.subtitle2,
					}}
					onClick={onClose}
					text='main.button_cancel'
				/>
				<RBHButton
					onClick={handleConfirmClick}
					style={{
						padding: '10px 24px',
						border: `1px solid ${disabled ? '#CDCDDC' : '#9012F5'}`,
						borderRadius: 48,
						color: Colors.white,
						height: '100%',
						backgroundColor: disabled ? Colors.grey400 : Colors.violet600,
						...textStyles.subtitle2,
					}}
					disabled={disabled}
					text='main.button_confirm'
				/>
			</Row>
		</Modal>
	);
};
